import React, { useCallback, useMemo, useRef, useState } from "react";
import { Chart } from "react-chartjs-2";
import moment from "moment";
import { useTranslation } from "react-i18next";
import DashboardChartCardKebabMenu from "./DashboardChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

const COLOR_AMOUNT = "#5CC7C1";
const COLOR_QTY = "#5C74C7";
const COLOR_CUSTOMER = "#F9BC44";

function formatLeftAxisTick(value) {
  const v = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(v)) return "";
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
  return String(Math.round(v));
}

/** @param {unknown} n */
function formatTableAmount(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(Math.round(v));
}

/** @param {unknown} n */
function formatTableMetric(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return String(Math.round(v));
}

const DATE_PARSE_FORMATS = ["DD/MM/YY", "D/M/YY", "DD/MM/YYYY"];

function compareSaleOverviewDateLabels(left, right) {
  const am = moment(String(left ?? ""), DATE_PARSE_FORMATS, true);
  const bm = moment(String(right ?? ""), DATE_PARSE_FORMATS, true);
  if (am.isValid() && bm.isValid()) return am.valueOf() - bm.valueOf();
  return String(left ?? "").localeCompare(String(right ?? ""));
}

/**
 * Sale Overview data table — sortable headers (dashboard range / API rows).
 *
 * @param {{ model: import('./dashboardChartsModel.js').SaleOverviewChartModel }} props
 */
function SaleOverviewDataTable({ model }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(() => ({
    key: /** @type {'date'|'customer'|'qty'|'amount'|null} */ (null),
    dir: /** @type {'asc'|'desc'} */ ("asc"),
  }));

  const rows = useMemo(() => {
    const labels = model?.labels ?? [];
    const amount = model?.amount ?? [];
    const qty = model?.qty ?? [];
    const customerCount = model?.customerCount ?? [];
    const n = Math.max(labels.length, amount.length, qty.length, customerCount.length);
    const out = [];
    for (let i = 0; i < n; i += 1) {
      out.push({
        key: `${String(labels[i] ?? "")}-${String(i)}`,
        label: labels[i] ?? "",
        customerCount: typeof customerCount[i] === "number" ? customerCount[i] : Number(customerCount[i]) || 0,
        qty: typeof qty[i] === "number" ? qty[i] : Number(qty[i]) || 0,
        amount: typeof amount[i] === "number" ? amount[i] : Number(amount[i]) || 0,
      });
    }
    return out;
  }, [model]);

  const sortedRows = useMemo(() => {
    if (!sort.key) return rows;
    const mul = sort.dir === "asc" ? 1 : -1;
    const copy = [...rows];
    copy.sort((a, b) => {
      let c = 0;
      if (sort.key === "date") c = compareSaleOverviewDateLabels(a.label, b.label);
      else if (sort.key === "customer") c = a.customerCount - b.customerCount;
      else if (sort.key === "qty") c = a.qty - b.qty;
      else if (sort.key === "amount") c = a.amount - b.amount;
      return c * mul;
    });
    return copy;
  }, [rows, sort.key, sort.dir]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  return (
    <div className="pos-dashboard-chart-table" role="table" aria-label={t("Sale Overview", { defaultValue: "Sale Overview" })}>
      <div className="pos-dashboard-chart-table__thead" role="rowgroup">
        <div className="pos-dashboard-chart-table__tr pos-dashboard-chart-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--first"
            ariaSortLabel={t("Sort by Date", { defaultValue: "Sort by Date" })}
            sorted={sort.key === "date"}
            direction={sort.dir}
            onSort={() => toggleSort("date")}
          >
            {t("Date", { defaultValue: "Date" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--num"
            ariaSortLabel={t("Sort by Customer", { defaultValue: "Sort by Customer" })}
            sorted={sort.key === "customer"}
            direction={sort.dir}
            onSort={() => toggleSort("customer")}
          >
            {t("Customer", { defaultValue: "Customer" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--num"
            ariaSortLabel={t("Sort by Qty", { defaultValue: "Sort by Qty" })}
            sorted={sort.key === "qty"}
            direction={sort.dir}
            onSort={() => toggleSort("qty")}
          >
            {t("Qty", { defaultValue: "Qty" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--last pos-dashboard-chart-table__th--num"
            ariaSortLabel={t("Sort by Amount", { defaultValue: "Sort by Amount" })}
            sorted={sort.key === "amount"}
            direction={sort.dir}
            onSort={() => toggleSort("amount")}
          >
            {t("Amount", { defaultValue: "Amount" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-dashboard-chart-table__scroll" role="rowgroup">
        {sortedRows.map((row) => (
          <div key={row.key} className="pos-dashboard-chart-table__tr" role="row">
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--label" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{row.label}</span>
            </div>
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--num" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{formatTableMetric(row.customerCount)}</span>
            </div>
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--num" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{formatTableMetric(row.qty)}</span>
            </div>
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--amount" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{formatTableAmount(row.amount)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {import('./dashboardChartsModel.js').SaleOverviewChartModel} props.model
 * @param {() => void} [props.onMenuChart]
 * @param {() => void} [props.onMenuTable]
 * @param {() => void} [props.onMenuDownload]
 * @param {() => void} [props.onMenuRemove]
 */
function SaleOverviewChartCard({ model, onMenuChart, onMenuTable, onMenuDownload, onMenuRemove }) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState("chart");
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("sale-overview"));
    onMenuDownload?.();
  }, [onMenuDownload]);

  const handleViewModeChange = useCallback(
    (mode) => {
      setViewMode(mode);
      if (mode === "chart") onMenuChart?.();
      if (mode === "table") onMenuTable?.();
    },
    [onMenuChart, onMenuTable],
  );

  const { chartData, options } = useMemo(() => {
    const leftTitle = model?.leftAxisLabel ?? "Amount";
    const rightTitle = model?.rightAxisLabel ?? "Value";
    const labels = model?.labels ?? [];
    const amount = model?.amount ?? [];
    const qty = model?.qty ?? [];
    const customerCount = model?.customerCount ?? [];

    const data = {
      labels,
      datasets: [
        {
          type: "bar",
          label: t("Amount", { defaultValue: "Amount" }),
          data: amount,
          backgroundColor: COLOR_AMOUNT,
          yAxisID: "y",
          order: 2,
          maxBarThickness: 28,
          borderRadius: 0,
          borderSkipped: false,
        },
        {
          type: "line",
          label: t("Qty", { defaultValue: "Qty" }),
          data: qty,
          borderColor: COLOR_QTY,
          backgroundColor: COLOR_QTY,
          tension: 0,
          pointStyle: "circle",
          pointRadius: 4,
          pointHoverRadius: 5,
          pointBorderWidth: 0,
          pointBackgroundColor: COLOR_QTY,
          borderWidth: 2,
          yAxisID: "y1",
          order: 0,
        },
        {
          type: "line",
          label: t("Customer", { defaultValue: "Customer" }),
          data: customerCount,
          borderColor: COLOR_CUSTOMER,
          backgroundColor: COLOR_CUSTOMER,
          tension: 0,
          pointStyle: "rectRot",
          pointRadius: 4,
          pointHoverRadius: 5,
          pointBorderWidth: 0,
          pointBackgroundColor: COLOR_CUSTOMER,
          borderWidth: 2,
          yAxisID: "y1",
          order: 1,
        },
      ],
    };

    const labelCount = labels.length;

    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          callbacks: {
            title: (items) => {
              const item = items[0];
              if (!item) return [];
              const chart = item.chart;
              const labels = chart?.data?.labels ?? [];
              const raw = labels[item.dataIndex];
              const text = raw != null ? String(raw) : "";
              return text ? [`${text}:`] : [];
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#666666",
            font: {
              family: "Roboto, sans-serif",
              size: 11,
            },
            maxRotation: labelCount > 10 ? 45 : 0,
            minRotation: 0,
            autoSkip: labelCount > 18,
          },
        },
        y: {
          position: "left",
          title: {
            display: true,
            text: leftTitle,
            color: "#666666",
            font: {
              family: "Roboto, sans-serif",
              size: 11,
            },
          },
          ticks: {
            color: "#666666",
            font: {
              family: "Roboto, sans-serif",
              size: 11,
            },
            callback: (v) => formatLeftAxisTick(v),
          },
          grid: {
            color: "#E6E6E6",
            drawTicks: false,
          },
          border: {
            display: false,
          },
        },
        y1: {
          position: "right",
          title: {
            display: true,
            text: rightTitle,
            color: "#666666",
            font: {
              family: "Roboto, sans-serif",
              size: 11,
            },
          },
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            color: "#666666",
            font: {
              family: "Roboto, sans-serif",
              size: 11,
            },
          },
          border: {
            display: false,
          },
        },
      },
    };

    return { chartData: data, options: opts };
  }, [model, t]);

  return (
    <div className="pos-dashboard-chart-card pos-dashboard-chart-card--sale-overview">
      <header className="pos-dashboard-chart-card__header">
        <h2 className="pos-dashboard-chart-card__title">{t("Sale Overview", { defaultValue: "Sale Overview" })}</h2>
        <DashboardChartCardKebabMenu
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          onDownload={handleDownloadPng}
          onRemove={onMenuRemove}
        />
      </header>
      <div className={`pos-dashboard-chart-card__body ${viewMode === "table" ? "pos-dashboard-chart-card__body--table" : ""}`}>
        <div ref={captureRef} className="pos-dashboard-chart-card__capture">
          {viewMode === "table" ? (
            <SaleOverviewDataTable model={model} />
          ) : (
            <>
              <div className="pos-dashboard-chart-card__canvas">
                <Chart type="bar" data={chartData} options={options} />
              </div>
              <div className="pos-sale-overview-legend" role="list" aria-label={t("Sale Overview legend", { defaultValue: "Sale Overview legend" })}>
                <div className="pos-sale-overview-legend__item" role="listitem">
                  <span className="pos-sale-overview-legend__amount-dot" aria-hidden />
                  <span>{t("Amount", { defaultValue: "Amount" })}</span>
                </div>
                <div className="pos-sale-overview-legend__item" role="listitem">
                  <span className="pos-sale-overview-legend__line-circle" aria-hidden />
                  <span>{t("Qty", { defaultValue: "Qty" })}</span>
                </div>
                <div className="pos-sale-overview-legend__item" role="listitem">
                  <span className="pos-sale-overview-legend__line-diamond" aria-hidden />
                  <span>{t("Customer", { defaultValue: "Customer" })}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SaleOverviewChartCard;
